import { createActions } from 'redux-actions';
import ActionFactory from './ActionFactory';
import {
  createAlert,
  createSuccessAlert,
  createDangerAlert,
} from './ActionCreators';
import {
  USER,
  SIGN_UP,
  LOG_IN,
  LOG_OUT,
  CREATE_USER,
  UPDATE_USER,
  DEACTIVATE_USER,
  GET_USER_PROFILE,
  USER_PROFILE_MODIFIED,
  CHANGE_USER_LANGUAGE,
  VERIFY_ACCESS_TOKEN,
  CHANGE_USER_TEAM,
  GET_JOINED_TEAM_LIST,
  QUICK_SIGNUP,
  GET_SIGNUP_LIST,
  APPROVE_SIGNUP,
  DECLINE_SIGNUP,
  GET_CHECKOUT_TOKEN,
  REQUEST_DEMO,
  CLEAR_CHECKOUT_TOKEN,
} from '../constants/ActionTypes';
import {
  UserAPIConnectors,
} from '../api';
import i18n from '../config/i18n';
import {
  removeUserAuthDataFromLocalStorage,
  setUserAuthDataToLocalStorage,
} from '../utils/index';

const plainActions = createActions(
  USER_PROFILE_MODIFIED,
  CHANGE_USER_LANGUAGE,
  CLEAR_CHECKOUT_TOKEN,
  ...Object.values(ActionFactory(SIGN_UP)),
  ...Object.values(ActionFactory(LOG_IN)),
  ...Object.values(ActionFactory(LOG_OUT)),
  ...Object.values(ActionFactory(CREATE_USER)),
  ...Object.values(ActionFactory(UPDATE_USER)),
  ...Object.values(ActionFactory(DEACTIVATE_USER)),
  ...Object.values(ActionFactory(GET_USER_PROFILE)),
  ...Object.values(ActionFactory(VERIFY_ACCESS_TOKEN)),
  ...Object.values(ActionFactory(CHANGE_USER_TEAM)),
  ...Object.values(ActionFactory(GET_JOINED_TEAM_LIST)),
  ...Object.values(ActionFactory(QUICK_SIGNUP)),
  ...Object.values(ActionFactory(GET_SIGNUP_LIST)),
  ...Object.values(ActionFactory(APPROVE_SIGNUP)),
  ...Object.values(ActionFactory(DECLINE_SIGNUP)),
  ...Object.values(ActionFactory(GET_CHECKOUT_TOKEN)),
  ...Object.values(ActionFactory(REQUEST_DEMO)),
  { prefix: USER },
);

const thunkActions = {
  signUp: userData => (
    async (dispatch) => {
      try {
        dispatch(plainActions.signUpRequest());
        await UserAPIConnectors.signUp(userData);
        dispatch(plainActions.signUpSuccess());
        dispatch(createSuccessAlert(i18n.t('signUpSucceedMsg')));
        return true;
      } catch (err) {
        dispatch(plainActions.signUpFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  logIn: (email, password, options) => (
    async (dispatch) => {
      const {
        onSucceed,
        onFail,
      } = options;

      try {
        dispatch(plainActions.logInRequest());
        const result = await UserAPIConnectors.logIn(email, password);
        dispatch(plainActions.logInSuccess(result));
        setUserAuthDataToLocalStorage(result);
        if (typeof onSucceed === 'function') {
          await onSucceed();
        }
        return true;
      } catch (err) {
        dispatch(plainActions.logInFailure());
        if (typeof onFail === 'function') {
          await onFail(err);
        } else {
          dispatch(createAlert(err));
        }
      }
      return false;
    }
  ),
  logOut: options => (
    async (dispatch) => {
      const {
        onSucceed,
      } = options;

      try {
        dispatch(plainActions.logOutRequest());
        await UserAPIConnectors.logOut();
        dispatch(plainActions.logOutSuccess());
        removeUserAuthDataFromLocalStorage();
        if (typeof onSucceed === 'function') {
          await onSucceed();
        }
        return true;
      } catch (err) {
        dispatch(plainActions.logOutFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  createUser: userData => (
    async (dispatch) => {
      try {
        dispatch(plainActions.createUserRequest());
        await UserAPIConnectors.createUser(userData);
        dispatch(plainActions.createUserSuccess());
        return true;
      } catch (err) {
        dispatch(plainActions.createUserFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  updateUser: userData => (
    async (dispatch) => {
      try {
        dispatch(plainActions.updateUserRequest());
        const result = await UserAPIConnectors.updateUser(userData);
        dispatch(plainActions.updateUserSuccess(result));
        return true;
      } catch (err) {
        dispatch(plainActions.updateUserFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  deactivateUser: userId => (
    async (dispatch) => {
      try {
        dispatch(plainActions.deactivateUserRequest());
        await UserAPIConnectors.deactivateUser(userId);
        dispatch(plainActions.deactivateUserSuccess());
        dispatch(createSuccessAlert(i18n.t('deactivateSucceedMsg')));
        return true;
      } catch (err) {
        dispatch(plainActions.deactivateUserFailure());
        dispatch(createDangerAlert(i18n.t('deactivateFailMsg')));
      }
      return false;
    }
  ),
  getUserProfile: () => (
    async (dispatch) => {
      try {
        dispatch(plainActions.getUserProfileRequest());
        const result = await UserAPIConnectors.getUser();
        dispatch(plainActions.getUserProfileSuccess(result));
        return true;
      } catch (err) {
        dispatch(plainActions.getUserProfileFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  isAccessTokenExist: options => (
    async (dispatch) => {
      const { onFail } = options;
      try {
        dispatch(plainActions.verifyAccessTokenRequest());
        const result = await UserAPIConnectors.checkAccessTokenExists();
        dispatch(plainActions.verifyAccessTokenSuccess(result));
        return true;
      } catch (err) {
        dispatch(plainActions.verifyAccessTokenFailure());
        if (typeof onFail === 'function') {
          await onFail(err);
        }
      }
      return false;
    }
  ),
  getJoinedTeamList: () => (
    async (dispatch) => {
      try {
        dispatch(plainActions.getJoinedTeamListRequest());
        const result = await UserAPIConnectors.getJoinedTeamList();
        dispatch(plainActions.getJoinedTeamListSuccess(result));
        return true;
      } catch (err) {
        dispatch(plainActions.getJoinedTeamListFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  quickSignup: userData => (
    async (dispatch) => {
      try {
        dispatch(plainActions.quickSignupRequest());
        await UserAPIConnectors.quickSignup(userData);
        dispatch(plainActions.quickSignupSuccess());
        dispatch(createSuccessAlert(i18n.t('signUpSucceedMsg')));
        return true;
      } catch (err) {
        dispatch(plainActions.quickSignupFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  getSignupList: () => (
    async (dispatch) => {
      try {
        dispatch(plainActions.getSignupListRequest());
        const result = await UserAPIConnectors.getSignupList();
        dispatch(plainActions.getSignupListSuccess(result));
        return true;
      } catch (err) {
        dispatch(plainActions.getSignupListFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  approveSignup: userData => (
    async (dispatch) => {
      try {
        dispatch(plainActions.approveSignupRequest());
        await UserAPIConnectors.approveSignup(userData);
        dispatch(plainActions.approveSignupSuccess());
        await dispatch(thunkActions.getSignupList());
        return true;
      } catch (err) {
        dispatch(plainActions.approveSignupFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  declineSignup: userId => (
    async (dispatch) => {
      try {
        dispatch(plainActions.declineSignupRequest());
        await UserAPIConnectors.declineSignup(userId);
        dispatch(plainActions.declineSignupSuccess());
        await dispatch(thunkActions.getSignupList());
        return true;
      } catch (err) {
        dispatch(plainActions.declineSignupFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  getCheckoutToken: (teamId, planKey) => (
    async (dispatch) => {
      try {
        dispatch(plainActions.getCheckoutTokenRequest());
        const result = await UserAPIConnectors.getCheckoutToken(teamId, planKey);
        dispatch(plainActions.getCheckoutTokenSuccess(result));
        return true;
      } catch (err) {
        dispatch(plainActions.getCheckoutTokenFailure());
        dispatch(createAlert(err));
      }
      return false;
    }
  ),
  requestDemo: userData => (
    async (dispatch) => {
      try {
        dispatch(plainActions.requestDemoRequest());
        await UserAPIConnectors.requestDemo(userData);
        dispatch(plainActions.requestDemoSuccess());
        dispatch(createSuccessAlert(i18n.t(['requestDemo', 'successMsg'])));
      } catch (err) {
        dispatch(plainActions.requestDemoFailure());
        dispatch(createAlert(err));
      }
    }
  ),
};

export default {
  ...plainActions,
  ...thunkActions,
};



// WEBPACK FOOTER //
// ./src/actions/UserActionCreators.js